import { ClipboardCheck, Hammer, FileSearch, Network, Layers, HardHat, KeyRound } from "lucide-react";

export const IMG = {
  team: "/images/the-family-placeholder.jpg",
  hero: "https://images.unsplash.com/photo-1669003985158-8fd34b5a4545?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1800",
  facade: "https://images.unsplash.com/photo-1653280679689-078c04c417a1?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  carpentry: "https://images.unsplash.com/photo-1505798577917-a65157d3320a?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  inspection: "https://images.unsplash.com/photo-1771240550356-8216bef58a3c?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1400",
  concrete: "https://images.unsplash.com/photo-1768677903496-becc4be07258?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
  waste: "https://images.unsplash.com/photo-1635691315495-ff39debe5764?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
  city: "https://images.unsplash.com/photo-1669003985893-345c97a103f0?crop=entropy&cs=srgb&fm=jpg&ixlib=rb-4.1.0&q=85&w=1600",
};

export const HERO_OPS = [
  "Inspect",
  "Document",
  "Report",
  "Solve",
  "Repair",
  "Renovate",
  "Contract & Coordinate",
  "Logistics",
  "Consulting",
  "Real Estate Services",
];

export const NAV_LINKS = [
  { label: "Property Operations", id: "operations" },
  { label: "Services", id: "services" },
  { label: "How It Works", id: "process" },
  { label: "About", id: "about" },
  { label: "Waste Solutions", id: "waste" },
  { label: "Contact", id: "contact" },
];

export const METRICS = [
  { value: "25+", label: "Years Combined Experience" },
  { value: "100%", label: "Photographic Documentation" },
  { value: "Full", label: "Work-Order Integration" },
  { value: "Ottawa", label: "Dedicated Local Response" },
];

export const PILLARS = [
  "Documented, Not Just Done",
  "One Vendor, Fewer Moving Parts",
  "25+ Years of Combined Experience",
  "Property Managers",
  "Condominium Corporations",
  "Commercial Buildings",
];

export const STEPS = [
  {
    key: "inspect",
    title: "Inspect",
    lead: "We attend the property before we price anything.",
    body: "Attend property, confirm conditions, and identify underlying deficiencies before pricing — so the scope reflects what is actually there.",
    deliverables: ["Site attendance", "Condition confirmation", "Root-cause findings"],
    img: IMG.inspection,
  },
  {
    key: "document",
    title: "Document",
    lead: "A clear starting record, on file.",
    body: "Photograph and log the starting state of every area we touch, giving management a clear starting record on file.",
    deliverables: ["Before photos", "Logged conditions", "Timestamped record"],
    img: IMG.facade,
  },
  {
    key: "report",
    title: "Report",
    lead: "Plain language. Real options.",
    body: "Send plain-language scope, options, and decision points to management — no jargon, no chasing.",
    deliverables: ["Written scope", "Options & pricing", "Decision points"],
    img: IMG.city,
  },
  {
    key: "solve",
    title: "Solve",
    lead: "In-house work, built to last.",
    body: "Execute the work in-house to long-term quality standards, not short-term patches.",
    deliverables: ["In-house crews", "Long-term standards", "Progress updates"],
    img: IMG.carpentry,
  },
  {
    key: "coordinate",
    title: "Coordinate",
    lead: "One point of accountability.",
    body: "Manage and oversee licensed specialty trades, closing out with full completion documentation.",
    deliverables: ["Trade oversight", "Completion photos", "Close-out report"],
    img: IMG.concrete,
  },
];

export const SERVICES = [
  {
    title: "Property Operations",
    tag: "Ongoing",
    icon: ClipboardCheck,
    items: ["Inspections", "Documentation", "Work-order response", "Recurring maintenance", "Superintendent support"],
    img: IMG.city,
    span: "lg:col-span-2",
  },
  {
    title: "Repairs",
    tag: "In-house",
    icon: Hammer,
    items: ["Carpentry", "Drywall", "Doors", "Fencing", "Building envelope repairs", "Small-to-mid projects"],
  },
  {
    title: "Assessments & Documentation",
    tag: "Reporting",
    icon: FileSearch,
    items: ["Property condition reports", "Damage condition reports", "Photographic logs", "Scope development"],
  },
  {
    title: "Project Coordination",
    tag: "Build",
    icon: HardHat,
    items: ["Large Renovations", "Additions", "Custom Home Builds"],
  },
  {
    title: "General Contracting",
    tag: "Licensed trades",
    icon: Network,
    items: ["Electrical", "Plumbing", "HVAC", "Roofing", "Single-point coordination"],
  },
  {
    title: "Concrete & Site Services",
    tag: "Exterior",
    icon: Layers,
    items: ["Concrete repairs", "Driveways", "Site Prep", "Material Coordination", "Trip-hazard mitigation", "Pads & landings", "Walkways"],
    img: IMG.concrete,
    span: "lg:col-span-2",
  },
  {
    title: "Real Estate Consulting & Services",
    tag: "In-house agent",
    icon: KeyRound,
    desc: "With Randy Blondin serving as our dedicated in-house Real Estate Agent and Consultant, we help clients make smarter property decisions informed by functional design and maximized return on investment (ROI). From initial project planning to full licensed real estate transactions, having dedicated representation in-house ensures seamless, end-to-end guidance from foundation to market.",
    items: [],
  },
];

export const WHY = [
  { no: "01", title: "Documentation", body: "Board- and insurer-ready photo reports without rework." },
  { no: "02", title: "Communication", body: "Transparent expectations. Zero chasing required." },
  { no: "03", title: "Operational Reach", body: "One vendor handling repairs, emergencies, and trade oversight." },
  {
    no: "04",
    title: "Accountability & Legacy",
    body: "A family business founded on legacy — Lakota Horlin & Richard Horlin — built for long-term, multi-year partnerships.",
  },
];

export const AREAS = [
  { name: "Downtown Ottawa", region: "Central", x: 59, y: 37 },
  { name: "Rockcliffe", region: "Central East", x: 64, y: 30 },
  { name: "Westboro", region: "West Central", x: 44, y: 42 },
  { name: "Nepean", region: "South West · HQ", x: 43, y: 56, hq: true },
  { name: "Kanata", region: "West", x: 22, y: 49 },
  { name: "Barrhaven", region: "South", x: 40, y: 71 },
  { name: "Orleans", region: "East", x: 82, y: 36 },
  { name: "Manotick", region: "Rural South", x: 52, y: 87 },
  { name: "West Carleton", region: "Rural West", x: 10, y: 24 },
];

export const FAQS = [
  {
    q: "Do you work with property management companies and condo corporations?",
    a: "Yes, our entire operational model is designed specifically for portfolio managers and condo boards.",
  },
  {
    q: "How is the work priced?",
    a: "Transparent scopes, unit pricing, or quote-based estimates with complete upfront photographic documentation.",
  },
  {
    q: "How do I send you a work order?",
    a: "Directly through our portal, Jobber integration, or email dispatch.",
  },
  {
    q: "Do you self-perform licensed trades such as electrical or plumbing?",
    a: "We handle general contracting and operations in-house, and coordinate licensed trades through vetted master partners under single-point accountability.",
  },
];
